import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import SimulationPortal from "./SimulationPortal";
import AIConversations from "./AIConversations";
import DigitalTwin from "./DigitalTwin";
import WelcomeModal from "./WelcomeModal";
import InterviewSimulator from "./InterviewSimulator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple auth context
const useAuth = () => {
  // This would normally come from a context, but for simplicity we'll manage it locally
  return {
    user: null, // This will be handled by the parent component
  };
};

// ===== SIMPLE LOGIN FORM =====
const LoginForm = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showConsent, setShowConsent] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'student'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('👤');
  const [availableIcons, setAvailableIcons] = useState([]);
  const [consentAgreed, setConsentAgreed] = useState(false);

  useEffect(() => {
    // Fetch available icons
    const fetchIcons = async () => {
      try {
        const response = await axios.get(`${API}/user-icons`);
        setAvailableIcons(response.data.icons);
      } catch (error) {
        console.error('Error fetching icons:', error);
      }
    };
    fetchIcons();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLogin && !consentAgreed) {
      setShowConsent(true);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isLogin) {
        // Login
        const loginData = new FormData();
        loginData.append('username', formData.email);
        loginData.append('password', formData.password);

        const response = await axios.post(`${API}/auth/login`, loginData);
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        onLogin(userData);
      } else {
        // Register
        const registerData = { ...formData, profile_icon: selectedIcon };
        await axios.post(`${API}/auth/register`, registerData);
        setSuccess('Account created! Please check your email to verify your account, then sign in.');
        setIsLogin(true);
        setError('');
        setConsentAgreed(false);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleConsentAccept = () => {
    setConsentAgreed(true);
    setShowConsent(false);
  };

  // Consent Modal
  if (showConsent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-800 to-purple-800 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
          <div className="p-8">
            <div className="text-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_edu-thinktank/artifacts/qt5sljf8_4B557135-B3FD-4CC5-AED3-033D06884500.jpg" 
                alt="NET2 Logo" 
                className="w-20 h-20 rounded-full mx-auto mb-4 object-cover"
              />
              <h1 className="text-2xl font-bold text-gray-800">Terms of Service & Consent</h1>
              <p className="text-gray-600 mt-2">Naplancer Educational Think Tank For All Inc.</p>
            </div>

            <div className="space-y-6 text-left max-h-96 overflow-y-auto bg-gray-50 p-6 rounded-lg">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">📋 NET2 For All Policies & Procedures</h3>
                <p className="text-gray-700 mb-4">
                  By creating an account with NET2ForAll.org, you agree to follow all policies and procedures established by 
                  Naplancer Educational Think Tank For All Inc., including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Respectful communication and behavior in all platform interactions</li>
                  <li>Proper use of educational resources and materials</li>
                  <li>Compliance with community guidelines and standards</li>
                  <li>Accurate representation of personal information</li>
                  <li>Responsible sharing of educational content</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">🔒 Data Collection & Privacy</h3>
                <p className="text-gray-700 mb-4">
                  You consent to allow Naplancer Educational Think Tank For All Inc. to collect, store, and use your information for organizational purposes, including:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Personal Information:</strong> Name, email, profile data, and educational preferences</li>
                  <li><strong>Usage Data:</strong> Learning progress, course interactions, and platform activity</li>
                  <li><strong>Communication:</strong> Messages, posts, and community interactions</li>
                  <li><strong>Educational Content:</strong> Submissions, assignments, and learning materials</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">🎯 Purpose of Data Collection</h3>
                <p className="text-gray-700">
                  We collect this information to provide you with personalized educational services, improve our platform, 
                  ensure community safety, comply with legal requirements, and advance our mission of nurturing potential 
                  in all learners.
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-blue-800 text-sm">
                  <strong>Mission Statement:</strong> "All Children Are Good, Let's Nurture Their Potential" - 
                  We are committed to creating an inclusive educational environment that respects your privacy while 
                  fostering growth and learning.
                </p>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setShowConsent(false)}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel Registration
              </button>
              <button
                onClick={handleConsentAccept}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                I Agree to Terms & Consent to Data Collection
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-800 to-purple-800">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_edu-thinktank/artifacts/qt5sljf8_4B557135-B3FD-4CC5-AED3-033D06884500.jpg" 
            alt="NET2 Logo" 
            className="w-20 h-20 rounded-full mx-auto mb-4 object-cover"
          />
          <h1 className="text-2xl font-bold text-gray-800">
            {isLogin ? 'Welcome Back' : 'Join NET2ForAll.org'}
          </h1>
          <p className="text-gray-600 mt-2">Naplancer Educational Think Tank For All Inc.</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {!isLogin && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                </select>
              </div>

              {/* Profile Icon Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Choose Your Profile Icon
                </label>
                <div className="grid grid-cols-8 gap-2 max-h-32 overflow-y-auto border rounded-lg p-3">
                  {availableIcons.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setSelectedIcon(icon)}
                      className={`w-10 h-10 text-2xl rounded-lg transition-all ${
                        selectedIcon === icon
                          ? 'bg-blue-500 text-white scale-110'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">Selected: {selectedIcon}</p>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-lg font-medium transition-all ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setError('');
              setSuccess('');
              setConsentAgreed(false);
            }}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
          </button>
        </div>

        {!isLogin && (
          <div className="mt-4 text-center text-xs text-gray-500">
            By creating an account, you agree to our Terms of Service and consent to data collection practices.
          </div>
        )}
      </div>
    </div>
  );
};

// ===== MAIN HEADER =====
const Header = ({ currentView, setCurrentView, user, onLogout }) => {
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'courses', label: 'Courses', icon: '📚' },
    { id: 'simulation', label: 'VR/AR Portal', icon: '🥽' },
    { id: 'interview', label: 'Interview Practice', icon: '💼' },
    { id: 'ai-conversations', label: 'AI Conversations', icon: '💬' },
    { id: 'digitaltwin', label: 'Digital Twin', icon: '🎮' },
    { id: 'community', label: 'Community', icon: '🌐' },
    { id: 'resources', label: 'Resources', icon: '📁' },
    { id: 'profile', label: 'Profile', icon: '👤' }
  ];

  // Add admin menu for admins
  if (user?.role === 'admin') {
    navItems.push({ id: 'admin', label: 'Admin Panel', icon: '⚙️' });
  }

  return (
    <header className="bg-gradient-to-r from-blue-800 to-purple-800 text-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_edu-thinktank/artifacts/qt5sljf8_4B557135-B3FD-4CC5-AED3-033D06884500.jpg" 
              alt="NET2 Logo" 
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <h1 className="text-2xl font-bold">Naplancer Educational Think Tank for all</h1>
              <p className="text-blue-200 text-sm">Nurturing Potential through Measurable Impact</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Donation Button */}
            <a
              href="https://givebutter.com/NET2educational"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              ❤️ Support Us
            </a>
            
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                {user?.first_name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-blue-200 capitalize">{user?.role}</p>
              </div>
            </div>
            
            <button
              onClick={onLogout}
              className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-sm transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
        
        <nav className="mt-4">
          <div className="flex flex-wrap gap-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setCurrentView(item.id)}
                className={`px-4 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                  currentView === item.id
                    ? 'bg-white text-blue-800 shadow-md'
                    : 'bg-blue-700 hover:bg-blue-600 text-white'
                }`}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>
    </header>
  );
};

// ===== DASHBOARD COMPONENT =====
const Dashboard = ({ user, setCurrentView }) => {
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await axios.get(`${API}/users/${user.id}/dashboard`);
        setDashboardData(response.data);
      } catch (error) {
        console.error('Error fetching dashboard:', error);
        setDashboardData({
          enrolled_courses: [],
          total_courses: 0,
          completed_videos: 0,
          recent_posts: 0,
          unread_messages: 0
        });
      }
    };

    if (user) {
      fetchDashboard();
    }
  }, [user]);

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Welcome back, {user?.first_name}!</h2>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="bg-blue-500 text-white p-3 rounded-full">
              <span className="text-2xl">📚</span>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold">Enrolled Courses</h3>
              <p className="text-3xl font-bold text-blue-600">{dashboardData.total_courses}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="bg-green-500 text-white p-3 rounded-full">
              <span className="text-2xl">✅</span>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold">Completed Videos</h3>
              <p className="text-3xl font-bold text-green-600">{dashboardData.completed_videos}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="bg-purple-500 text-white p-3 rounded-full">
              <span className="text-2xl">📝</span>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold">Recent Posts</h3>
              <p className="text-3xl font-bold text-purple-600">{dashboardData.recent_posts}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="bg-orange-500 text-white p-3 rounded-full">
              <span className="text-2xl">💬</span>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold">Messages</h3>
              <p className="text-3xl font-bold text-orange-600">{dashboardData.unread_messages}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button 
            onClick={() => setCurrentView('courses')}
            className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg text-left transition-colors"
          >
            <div className="text-2xl mb-2">🎓</div>
            <h4 className="font-semibold">Browse Courses</h4>
            <p className="text-sm text-gray-600">Explore available courses</p>
          </button>
          <button 
            onClick={() => setCurrentView('ai-conversations')}
            className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg text-left transition-colors"
          >
            <div className="text-2xl mb-2">💬</div>
            <h4 className="font-semibold">AI Conversations</h4>
            <p className="text-sm text-gray-600">Practice with AI personas</p>
          </button>
          <button 
            onClick={() => setCurrentView('community')}
            className="p-4 bg-green-50 hover:bg-green-100 rounded-lg text-left transition-colors"
          >
            <div className="text-2xl mb-2">🌐</div>
            <h4 className="font-semibold">Join Community</h4>
            <p className="text-sm text-gray-600">Connect with other learners</p>
          </button>
          <button 
            onClick={() => setCurrentView('resources')}
            className="p-4 bg-orange-50 hover:bg-orange-100 rounded-lg text-left transition-colors"
          >
            <div className="text-2xl mb-2">📁</div>
            <h4 className="font-semibold">Access Resources</h4>
            <p className="text-sm text-gray-600">Browse learning materials</p>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold mb-4">Recent Activity</h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center">
              📚
            </div>
            <div>
              <p className="font-medium">Welcome to NET2!</p>
              <p className="text-sm text-gray-600">Start exploring courses and connecting with the community</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== COURSES COMPONENT =====
const CoursesView = () => {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesResponse, categoriesResponse] = await Promise.all([
        axios.get(`${API}/courses`),
        axios.get(`${API}/categories`)
      ]);
      
      setCourses(coursesResponse.data);
      setCategories(categoriesResponse.data.categories);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = selectedCategory 
    ? courses.filter(course => course.category === selectedCategory)
    : courses;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-bold mb-4">Available Courses</h1>
        <p className="text-gray-600 mb-6">
          Explore our comprehensive educational programs designed for migrants and new Americans.
        </p>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('')}
            className={`px-4 py-2 rounded-lg transition-all ${
              selectedCategory === '' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All Categories
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg transition-all ${
                selectedCategory === category 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {filteredCourses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map((course) => (
            <div key={course.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
              <div className="h-48 bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                <div className="text-white text-6xl">📚</div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">{course.title}</h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{course.description}</p>
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">{course.category}</span>
                  <span>{course.difficulty_level}</span>
                </div>
                <div className="mt-4">
                  <p className="text-sm text-gray-600">Instructor: {course.instructor}</p>
                  <p className="text-sm text-gray-600">Videos: {course.videos?.length || 0}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📚</div>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No courses available</h3>
          <p className="text-gray-500">
            {selectedCategory ? `No courses found in "${selectedCategory}"` : 'Courses will be added soon.'}
          </p>
        </div>
      )}
    </div>
  );
};

// ===== SIMPLE PLACEHOLDER COMPONENTS =====
// ===== COMMUNITY VIEW WITH POSTS, MESSAGING, AND SOCIAL FEATURES =====
const CommunityView = () => {
  const [activeTab, setActiveTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [expandedComments, setExpandedComments] = useState({});
  const [newComment, setNewComment] = useState({});

  useEffect(() => {
    if (activeTab === 'posts') {
      fetchPosts();
    } else if (activeTab === 'messages') {
      fetchConversations();
    } else if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/search?query=${userSearch}`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/posts/${postId}/comments`);
      setExpandedComments(prev => ({
        ...prev,
        [postId]: response.data
      }));
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPostContent.trim()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/posts`, { content: newPostContent });
      setNewPostContent('');
      fetchPosts();
      alert('Post created successfully!');
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleLikePost = async (postId) => {
    try {
      await axios.post(`${API}/posts/${postId}/like`);
      fetchPosts();
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleDislikePost = async (postId) => {
    try {
      await axios.post(`${API}/posts/${postId}/dislike`);
      fetchPosts();
    } catch (error) {
      console.error('Error disliking post:', error);
    }
  };

  const handleAddComment = async (postId) => {
    const commentContent = newComment[postId];
    if (!commentContent || !commentContent.trim()) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('content', commentContent);
      
      await axios.post(`${API}/posts/${postId}/comments`, formData);
      setNewComment(prev => ({ ...prev, [postId]: '' }));
      fetchComments(postId);
      fetchPosts();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  const toggleComments = (postId) => {
    if (expandedComments[postId]) {
      const newExpanded = { ...expandedComments };
      delete newExpanded[postId];
      setExpandedComments(newExpanded);
    } else {
      fetchComments(postId);
    }
  };

  const handleFollowUser = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/follow`);
      alert('User followed successfully!');
      fetchUsers();
    } catch (error) {
      console.error('Error following user:', error);
    }
  };

  const handleUnfollowUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}/follow`);
      alert('User unfollowed successfully!');
      fetchUsers();
    } catch (error) {
      console.error('Error unfollowing user:', error);
    }
  };

  const handleStartConversation = async (user) => {
    setSelectedUser(user);
    setShowNewMessageModal(true);
  };

  const handleSendNewMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('recipient_id', selectedUser.id);
      formData.append('content', newMessage);
      
      await axios.post(`${API}/messages`, formData);
      setNewMessage('');
      setShowNewMessageModal(false);
      setActiveTab('messages');
      fetchConversations();
      alert('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('conversation_id', selectedConversation.id);
      formData.append('recipient_id', selectedConversation.participants.find(p => p !== selectedConversation.current_user_id));
      formData.append('content', newMessage);
      
      await axios.post(`${API}/messages`, formData);
      setNewMessage('');
      fetchMessages(selectedConversation.id);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md">
        {/* Tabs */}
        <div className="border-b">
          <div className="flex">
            <button
              onClick={() => setActiveTab('posts')}
              className={`px-6 py-4 font-medium ${
                activeTab === 'posts'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              📝 Posts & Feed
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-6 py-4 font-medium ${
                activeTab === 'messages'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              💬 Messages
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-4 font-medium ${
                activeTab === 'users'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              👥 Connect
            </button>
          </div>
        </div>

        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div className="p-6">
            {/* Create Post */}
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <form onSubmit={handleCreatePost}>
                <textarea
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  placeholder="What's on your mind? Share your thoughts with the community..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  rows="3"
                />
                <div className="mt-3 flex justify-end">
                  <button
                    type="submit"
                    disabled={loading || !newPostContent.trim()}
                    className={`px-6 py-2 rounded-lg font-medium ${
                      loading || !newPostContent.trim()
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {loading ? 'Posting...' : 'Post'}
                  </button>
                </div>
              </form>
            </div>

            {/* Posts Feed */}
            <div className="space-y-4">
              {posts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📝</div>
                  <p className="text-gray-600">No posts yet. Be the first to share something!</p>
                </div>
              ) : (
                posts.map((post) => (
                  <div key={post.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <div className="text-3xl">{post.author_icon || '👤'}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold">{post.author_name}</h4>
                            <p className="text-xs text-gray-500">{new Date(post.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                        <p className="mt-2 text-gray-700">{post.content}</p>
                        
                        {/* Like/Dislike/Comment Actions */}
                        <div className="mt-3 flex items-center space-x-4">
                          <button
                            onClick={() => handleLikePost(post.id)}
                            className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                              post.user_liked 
                                ? 'text-green-600 bg-green-50 px-3 py-1 rounded-full' 
                                : 'text-gray-600 hover:text-green-600 hover:bg-green-50 px-3 py-1 rounded-full'
                            }`}
                          >
                            <span>👍</span>
                            <span>{post.likes_count || 0}</span>
                          </button>
                          <button
                            onClick={() => handleDislikePost(post.id)}
                            className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                              post.user_disliked 
                                ? 'text-red-600 bg-red-50 px-3 py-1 rounded-full' 
                                : 'text-gray-600 hover:text-red-600 hover:bg-red-50 px-3 py-1 rounded-full'
                            }`}
                          >
                            <span>👎</span>
                            <span>{post.dislikes_count || 0}</span>
                          </button>
                          <button
                            onClick={() => toggleComments(post.id)}
                            className="flex items-center space-x-1 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 px-3 py-1 rounded-full transition-colors"
                          >
                            <span>💬</span>
                            <span>{post.comments_count || 0} Comments</span>
                          </button>
                        </div>

                        {/* Comments Section */}
                        {expandedComments[post.id] && (
                          <div className="mt-4 border-t pt-4 space-y-3">
                            {/* Add Comment */}
                            <div className="flex space-x-2">
                              <input
                                type="text"
                                value={newComment[post.id] || ''}
                                onChange={(e) => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                                placeholder="Write a comment..."
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddComment(post.id);
                                  }
                                }}
                              />
                              <button
                                onClick={() => handleAddComment(post.id)}
                                disabled={!newComment[post.id] || !newComment[post.id].trim()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:bg-gray-400"
                              >
                                Post
                              </button>
                            </div>

                            {/* Comments List */}
                            {expandedComments[post.id].map((comment) => (
                              <div key={comment.id} className="flex space-x-2 bg-gray-50 p-3 rounded-lg">
                                <div className="text-xl">{comment.author?.profile_picture || '👤'}</div>
                                <div className="flex-1">
                                  <h5 className="font-semibold text-sm">{comment.author?.name || 'User'}</h5>
                                  <p className="text-sm text-gray-700 mt-1">{comment.content}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {new Date(comment.created_at).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="flex h-[600px]">
            {/* Conversations List */}
            <div className="w-1/3 border-r overflow-y-auto">
              <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                <h3 className="font-semibold">Conversations</h3>
                <button
                  onClick={() => setActiveTab('users')}
                  className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                >
                  + New
                </button>
              </div>
              {conversations.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <div className="text-4xl mb-2">💬</div>
                  <p className="mb-4">No conversations yet</p>
                  <button
                    onClick={() => setActiveTab('users')}
                    className="text-sm bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  >
                    Start a conversation
                  </button>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => selectConversation(conv)}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                      selectedConversation?.id === conv.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">💬</div>
                      <div className="flex-1">
                        <h4 className="font-medium">{conv.participant_names?.join(', ') || 'Conversation'}</h4>
                        <p className="text-sm text-gray-500 truncate">{conv.last_message || 'No messages'}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Messages View */}
            <div className="flex-1 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Messages Header */}
                  <div className="p-4 border-b bg-gray-50">
                    <h3 className="font-semibold">{selectedConversation.participant_names?.join(', ')}</h3>
                  </div>

                  {/* Messages List */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.length === 0 ? (
                      <div className="text-center text-gray-500 mt-8">
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.is_sender ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs px-4 py-2 rounded-lg ${
                              msg.is_sender
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-800'
                            }`}
                          >
                            <p>{msg.content}</p>
                            <p className="text-xs mt-1 opacity-75">
                              {new Date(msg.timestamp || msg.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Send Message */}
                  <div className="p-4 border-t">
                    <form onSubmit={handleSendMessage} className="flex space-x-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="submit"
                        disabled={loading || !newMessage.trim()}
                        className={`px-6 py-2 rounded-lg font-medium ${
                          loading || !newMessage.trim()
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        Send
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <div className="text-6xl mb-4">💬</div>
                    <p>Select a conversation to view messages</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users/Connect Tab */}
        {activeTab === 'users' && (
          <div className="p-6">
            <div className="mb-6">
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                onKeyUp={(e) => e.key === 'Enter' && fetchUsers()}
                placeholder="Search users by name or email..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={fetchUsers}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Search
              </button>
            </div>

            <div className="space-y-3">
              {users.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">👥</div>
                  <p className="text-gray-600">Search for users to connect with</p>
                </div>
              ) : (
                users.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="text-3xl">{user.profile_icon}</div>
                      <div>
                        <h4 className="font-semibold">{user.name}</h4>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        <span className="text-xs px-2 py-1 bg-gray-100 rounded">{user.role}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleStartConversation(user)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        💬 Message
                      </button>
                      <button
                        onClick={() => handleFollowUser(user.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                      >
                        + Follow
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* New Message Modal */}
      {showNewMessageModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">New Message to {selectedUser.name}</h3>
              <button onClick={() => setShowNewMessageModal(false)} className="text-gray-500 hover:text-gray-700">
                <span className="text-2xl">×</span>
              </button>
            </div>
            <form onSubmit={handleSendNewMessage}>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                rows="4"
              />
              <div className="mt-4 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowNewMessageModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !newMessage.trim()}
                  className={`px-6 py-2 rounded-lg font-medium ${
                    loading || !newMessage.trim()
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ===== EDUCATIONAL CONTENT UPLOAD COMPONENT =====
const EducationalContentUpload = ({ onClose, onSuccess, user }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content_type: '',
    category: '',
    subject_area: '',
    grade_level: 'All Ages',
    difficulty_level: 'Beginner',
    estimated_duration: 0,
    tags: '',
    learning_objectives: '',
    prerequisites: ''
  });
  const [file, setFile] = useState(null);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/educational-content/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const uploadData = new FormData();
      Object.keys(formData).forEach(key => {
        uploadData.append(key, formData[key]);
      });
      uploadData.append('file', file);

      const response = await axios.post(`${API}/educational-content/upload`, uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      onSuccess && onSuccess(response.data);
      onClose && onClose();
    } catch (error) {
      setError(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Upload Educational Content</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <span className="text-2xl">×</span>
            </button>
          </div>
          <p className="text-gray-600 mt-2">Share educational materials with the NET2ForAll.org community</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter content title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content Type *</label>
              <select
                name="content_type"
                value={formData.content_type}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select content type</option>
                {categories.content_types?.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select category</option>
                {categories.categories?.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject Area *</label>
              <select
                name="subject_area"
                value={formData.subject_area}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select subject</option>
                {categories.subject_areas?.map((subject) => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Describe the educational content..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">File Upload *</label>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.mp4,.pptx,.docx,.xlsx,.zip,.jpg,.png,.mp3,.wav"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Supported formats: PDF, MP4, PPTX, DOCX, XLSX, ZIP, JPG, PNG, MP3, WAV (Max 100MB)
            </p>
          </div>

          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2 rounded-lg font-medium ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {loading ? 'Uploading...' : 'Upload Content'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== EDUCATIONAL CONTENT BROWSER COMPONENT =====
const EducationalContentView = ({ user }) => {
  const [content, setContent] = useState([]);
  const [categories, setCategories] = useState({});
  const [filters, setFilters] = useState({
    category: '',
    content_type: '',
    search: ''
  });
  const [showUpload, setShowUpload] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
    fetchContent();
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/educational-content/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchContent = async () => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });
      
      const response = await axios.get(`${API}/educational-content?${params}`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value
    });
  };

  const handleUploadSuccess = (result) => {
    fetchContent(); // Refresh content list
    alert(result.message + (result.note ? '\n\n' + result.note : ''));
  };

  const viewContent = async (contentId) => {
    try {
      const response = await axios.get(`${API}/educational-content/${contentId}`);
      setSelectedContent(response.data);
    } catch (error) {
      console.error('Error fetching content details:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold">Educational Content Library</h2>
          <p className="text-gray-600">Discover and share educational materials with the community</p>
        </div>
        {user && (
          <button
            onClick={() => setShowUpload(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <span>📚</span>
            <span>Upload Content</span>
          </button>
        )}
      </div>

      {/* Basic Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Filter Content</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Categories</option>
            {categories.categories?.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            value={filters.content_type}
            onChange={(e) => handleFilterChange('content_type', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Types</option>
            {categories.content_types?.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Search content..."
            className="px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      {/* Content Grid */}
      {content.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {content.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2 line-clamp-2">{item.title}</h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{item.description}</p>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Category:</span>
                    <span className="font-medium">{item.category}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Type:</span>
                    <span className="font-medium">{item.content_type}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{item.uploader?.profile_icon || '👤'}</span>
                    <div className="text-xs text-gray-500">
                      <p>by {item.uploader?.name || 'Unknown'}</p>
                      <p>{item.download_count || 0} downloads</p>
                    </div>
                  </div>
                  <button
                    onClick={() => viewContent(item.id)}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                  >
                    View Content
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📚</div>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No educational content found</h3>
          <p className="text-gray-500 mb-4">Be the first to upload educational materials!</p>
          {user && (
            <button
              onClick={() => setShowUpload(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Upload Content
            </button>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && user && (
        <EducationalContentUpload
          user={user}
          onClose={() => setShowUpload(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {/* Content Detail Modal */}
      {selectedContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">{selectedContent.title}</h2>
                <button 
                  onClick={() => setSelectedContent(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 mb-4">{selectedContent.description}</p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Category:</span>
                    <span className="ml-2 font-medium">{selectedContent.category}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <span className="ml-2 font-medium">{selectedContent.content_type}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Downloads:</span>
                    <span className="ml-2 font-medium">{selectedContent.download_count || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Uploader:</span>
                    <span className="ml-2 font-medium">{selectedContent.uploader?.name || 'Unknown'}</span>
                  </div>
                </div>
              </div>

              {selectedContent.file_url && (
                <div className="text-center">
                  <a
                    href={selectedContent.file_url}
                    download
                    className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 inline-flex items-center space-x-2"
                  >
                    <span>📥</span>
                    <span>Download Content</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ResourcesView = ({ user }) => <EducationalContentView user={user} />;

const ProfileView = ({ user }) => (
  <div className="max-w-4xl mx-auto p-6">
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="flex items-center space-x-6 mb-6">
        <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center text-white text-3xl">
          {user?.first_name?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <div>
          <h2 className="text-3xl font-bold">{user?.first_name} {user?.last_name}</h2>
          <p className="text-gray-600 capitalize text-lg">{user?.role}</p>
          <p className="text-gray-500">{user?.email}</p>
        </div>
      </div>
      
      <div className="border-t pt-6">
        <h3 className="text-xl font-semibold mb-4">Account Information</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Member Since</p>
            <p className="font-medium">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Recently joined'}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Status</p>
            <p className="font-medium text-green-600">Active</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AdminView = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [userList, setUserList] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchDetailedStats();
      fetchUserList();
      if (selectedTab === 'verifications') {
        fetchPendingVerifications();
      }
    }
  }, [user, selectedTab]);

  const fetchDetailedStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/detailed-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching detailed stats:', error);
    }
  };

  const fetchUserList = async () => {
    try {
      const response = await axios.get(`${API}/admin/user-export`);
      setUserList(response.data.users);
    } catch (error) {
      console.error('Error fetching user list:', error);
    }
  };

  const fetchPendingVerifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/pending-verifications`);
      setPendingVerifications(response.data.pending_verifications);
    } catch (error) {
      console.error('Error fetching pending verifications:', error);
      alert('Failed to fetch pending verifications');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to manually verify ${userName}'s email?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/verify-user/${userId}`);
      alert(response.data.message);
      fetchPendingVerifications(); // Refresh list
      fetchDetailedStats(); // Update stats
    } catch (error) {
      console.error('Error verifying user:', error);
      alert('Failed to verify user: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const handleResendVerification = async (userId, userName) => {
    if (!confirm(`Resend verification email to ${userName}?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/resend-verification/${userId}`);
      alert(response.data.message);
    } catch (error) {
      console.error('Error resending verification:', error);
      alert('Failed to resend verification: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  const setupAdminAccount = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/setup-admin-derrick`);
      alert(`Admin setup: ${response.data.message}`);
      if (response.data.temporary_password) {
        alert(`Temporary password: ${response.data.temporary_password}`);
      }
    } catch (error) {
      alert('Error setting up admin account');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="mb-4">You don't have permission to access the admin dashboard.</p>
          {user?.email === 'derrick@net2forall.org' || user?.first_name === 'Derrick' ? (
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-blue-800 mb-3">Hello Derrick! Set up your admin access:</p>
              <button
                onClick={setupAdminAccount}
                disabled={loading}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Setting up...' : 'Setup Admin Account'}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold">Admin Dashboard</h2>
          <p className="text-gray-600">Welcome, {user.first_name}! Here's your platform overview.</p>
        </div>
        <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
          Live Data
        </div>
      </div>
      
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Platform Overview', icon: '📊' },
            { id: 'verifications', label: 'Pending Verifications', icon: '📧' },
            { id: 'users', label: 'User Management', icon: '👥' },
            { id: 'signups', label: 'Recent Signups', icon: '📝' },
            { id: 'analytics', label: 'Analytics', icon: '📈' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                selectedTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Platform Overview Tab */}
      {selectedTab === 'overview' && stats && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.platform_overview.total_users}</p>
                  <p className="text-xs text-green-600">
                    {stats.platform_overview.verification_rate}% verified
                  </p>
                </div>
                <div className="text-blue-500 text-4xl">👥</div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">New This Week</p>
                  <p className="text-3xl font-bold text-green-600">{stats.growth_metrics.new_users_this_week}</p>
                  <p className="text-xs text-gray-500">
                    {stats.growth_metrics.new_users_this_month} this month
                  </p>
                </div>
                <div className="text-green-500 text-4xl">📈</div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active (24h)</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.growth_metrics.active_users_24h}</p>
                  <p className="text-xs text-gray-500">Recent activity</p>
                </div>
                <div className="text-purple-500 text-4xl">⚡</div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Content</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {stats.content_metrics.total_courses + stats.content_metrics.total_posts + stats.content_metrics.total_resources}
                  </p>
                  <p className="text-xs text-gray-500">Courses, posts & resources</p>
                </div>
                <div className="text-orange-500 text-4xl">📚</div>
              </div>
            </div>
          </div>

          {/* Detailed Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">User Demographics</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="flex items-center"><span className="mr-2">🎓</span>Students</span>
                  <span className="font-bold text-blue-600">{stats.user_demographics.students}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center"><span className="mr-2">👨‍🏫</span>Teachers</span>
                  <span className="font-bold text-green-600">{stats.user_demographics.teachers}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center"><span className="mr-2">⚙️</span>Admins</span>
                  <span className="font-bold text-purple-600">{stats.user_demographics.admins}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">Platform Health</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="flex items-center"><span className="mr-2">📧</span>Pending Verifications</span>
                  <span className="font-bold text-yellow-600">{stats.platform_health.email_verification_pending}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center"><span className="mr-2">📋</span>Content Awaiting Approval</span>
                  <span className="font-bold text-orange-600">{stats.platform_health.content_requiring_approval}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center"><span className="mr-2">💬</span>Active Conversations</span>
                  <span className="font-bold text-blue-600">{stats.platform_health.active_conversations}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Signups Tab */}
      {selectedTab === 'signups' && stats && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold mb-4">Recent User Signups</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-4 font-semibold">Name</th>
                  <th className="text-left py-3 px-4 font-semibold">Email</th>
                  <th className="text-left py-3 px-4 font-semibold">Role</th>
                  <th className="text-left py-3 px-4 font-semibold">Status</th>
                  <th className="text-left py-3 px-4 font-semibold">Signup Date</th>
                  <th className="text-left py-3 px-4 font-semibold">Last Login</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_signups.map((signup, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{signup.name}</td>
                    <td className="py-3 px-4 text-blue-600">{signup.email}</td>
                    <td className="py-3 px-4 capitalize">{signup.role}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        signup.verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {signup.verified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{signup.signup_date}</td>
                    <td className="py-3 px-4 text-gray-600">
                      {signup.last_login === 'Never' ? 'Never' : new Date(signup.last_login).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pending Verifications Tab */}
      {selectedTab === 'verifications' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-bold">Pending Email Verifications</h3>
              <p className="text-sm text-gray-600">Users who haven't verified their email addresses</p>
            </div>
            <button
              onClick={fetchPendingVerifications}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm disabled:bg-gray-400"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {pendingVerifications.length === 0 ? (
            <div className="text-center py-12">
              <span className="text-6xl">✅</span>
              <p className="text-gray-600 mt-4">No pending verifications! All users are verified.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-semibold">User</th>
                    <th className="text-left py-3 px-4 font-semibold">Email</th>
                    <th className="text-left py-3 px-4 font-semibold">Role</th>
                    <th className="text-left py-3 px-4 font-semibold">Signup Date</th>
                    <th className="text-left py-3 px-4 font-semibold">Token Status</th>
                    <th className="text-left py-3 px-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingVerifications.map((user, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl">{user.profile_icon}</span>
                          <span className="font-medium">{user.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-blue-600">{user.email}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 text-xs rounded-full capitalize bg-blue-100 text-blue-800">
                          {user.role}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600 text-sm">{user.signup_date}</td>
                      <td className="py-3 px-4">
                        {user.token_expired ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                            Expired
                          </span>
                        ) : user.has_token ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                            No Token
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleVerifyUser(user.id, user.name)}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                            title="Manually verify this user"
                          >
                            ✓ Verify
                          </button>
                          <button
                            onClick={() => handleResendVerification(user.id, user.name)}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                            title="Resend verification email"
                          >
                            📧 Resend
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 text-sm text-gray-600">
                <p>Total pending verifications: <span className="font-bold">{pendingVerifications.length}</span></p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* User Management Tab */}
      {selectedTab === 'users' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">All Users ({userList.length})</h3>
            <button
              onClick={fetchUserList}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
            >
              Refresh Data
            </button>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-3 px-4 font-semibold">Name</th>
                  <th className="text-left py-3 px-4 font-semibold">Email</th>
                  <th className="text-left py-3 px-4 font-semibold">Role</th>
                  <th className="text-left py-3 px-4 font-semibold">Status</th>
                  <th className="text-left py-3 px-4 font-semibold">Joined</th>
                  <th className="text-left py-3 px-4 font-semibold">Icon</th>
                </tr>
              </thead>
              <tbody>
                {userList.map((user, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{user.name}</td>
                    <td className="py-3 px-4 text-blue-600">{user.email}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                        user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        user.role === 'teacher' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <span className={`w-2 h-2 rounded-full ${
                          user.verified ? 'bg-green-500' : 'bg-yellow-500'
                        }`}></span>
                        <span className="text-sm">
                          {user.verified ? 'Verified' : 'Pending'}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{user.signup_date}</td>
                    <td className="py-3 px-4 text-2xl">{user.profile_icon}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {selectedTab === 'analytics' && stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">Content Analytics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>📚 Total Courses</span>
                  <span className="font-bold">{stats.content_metrics.total_courses}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>✅ Published Courses</span>
                  <span className="font-bold text-green-600">{stats.content_metrics.published_courses}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>🎥 Videos</span>
                  <span className="font-bold">{stats.content_metrics.total_videos}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>📄 Resources</span>
                  <span className="font-bold">{stats.content_metrics.total_resources}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">Engagement Analytics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span>📝 Total Posts</span>
                  <span className="font-bold">{stats.content_metrics.total_posts}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>👍 Likes</span>
                  <span className="font-bold text-blue-600">{stats.engagement_metrics.total_likes}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>💬 Comments</span>
                  <span className="font-bold text-green-600">{stats.engagement_metrics.total_comments}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>📩 Messages</span>
                  <span className="font-bold text-purple-600">{stats.engagement_metrics.total_messages}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===== MAIN APP COMPONENT =====
function App() {
  const [user, setUser] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setUser(response.data);
      
      // Show welcome modal if it's the user's first login
      if (response.data.is_first_login) {
        setShowWelcomeModal(true);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      handleLogout();
    }
  };

  useEffect(() => {
    // Check for stored token on app load
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchProfile();
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setCurrentView('dashboard');
    
    // Show welcome modal if it's the user's first login
    if (userData.is_first_login) {
      setShowWelcomeModal(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setCurrentView('dashboard');
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return <Dashboard user={user} setCurrentView={setCurrentView} />;
      case 'courses':
        return <CoursesView />;
      case 'simulation':
        return <SimulationPortal user={user} onBack={() => setCurrentView('dashboard')} />;
      case 'interview':
        return <InterviewSimulator user={user} onBack={() => setCurrentView('dashboard')} />;
      case 'ai-conversations':
        return <AIConversations user={user} onBack={() => setCurrentView('dashboard')} />;
      case 'digitaltwin':
        return <DigitalTwin user={user} onBack={() => setCurrentView('dashboard')} />;
      case 'community':
        return <CommunityView />;
      case 'resources':
        return <ResourcesView user={user} />;
      case 'profile':
        return <ProfileView user={user} />;
      case 'admin':
        return <AdminView user={user} />;
      default:
        return <Dashboard user={user} setCurrentView={setCurrentView} />;
    }
  };

  if (!user) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showWelcomeModal && user && (
        <WelcomeModal 
          user={user} 
          onClose={() => setShowWelcomeModal(false)}
        />
      )}
      <Header 
        currentView={currentView} 
        setCurrentView={setCurrentView}
        user={user}
        onLogout={handleLogout}
      />
      <main className="pb-8">
        {renderCurrentView()}
      </main>
    </div>
  );
}

export default App;
